import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { CreateInsuranceResponse } from '@havenengineering/module-owners-freshdesk/dist/types/insurance';
import InlineMessaging, {
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging/InlineMessaging';
import {
  DateInputField,
  DateValue,
} from '@havenengineering/module-shared-library/dist/form/components/DateInput';
import { TextInputField } from '@havenengineering/module-shared-library/dist/form/components/TextInput';
import useForm, {
  createRegexValidator,
  emailValidator,
} from '@havenengineering/module-shared-library/dist/form/hooks/useForm';
import { Alert } from '@havenengineering/module-shared-owners-ui/dist/components/Alert';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button';
import { SectionHeader } from '@havenengineering/module-shared-owners-ui/dist/components/SectionHeader';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';
import clsx from 'clsx';
import { DateTime } from 'luxon';

import { now } from '../../helpers/dateTime';
import { fetchWrapper, withBFFUrl } from '../../helpers/fetch';
import { saveFile } from '../../helpers/files';
import { insuranceConfirmation, pageView } from '../../helpers/googleTag';
import styles from '../../styles/pages/insurance.module.scss';

const FileUploader = dynamic(
  import('../../components/AttachmentUploader/AttachmentUploader'),
  {
    ssr: false,
  }
);
type InsuranceCertificate = {
  accountNumber: string;
  fullName: string;
  email: string;
  insuranceProvider: string;
  insuranceExpiryDate: DateValue;
};
interface Document {
  label: string;
  file: string;
}
const path = '/assets/pdf/';
const insuranceDocuments: Document[] = [
  {
    label: 'Copy of the Policy',
    file: 'Bourne_Leisure_Caravan_and_Lodges_arranged_Insurance.pdf',
  },
  { label: 'Important Information (Aviva)', file: 'Important_information.pdf' },
  {
    label: 'Insurance Product Information',
    file: 'Insurance_Product_Information_Document.pdf',
  },
  {
    label: 'Notice to Policyholders',
    file: 'Notice_to_policyholders.pdf',
  },
];
const claimDocuments: Document[] = [
  {
    label: 'Claim Form',
    file: 'Insurance_Claim_Form.pdf',
  },
  {
    label: 'Claim BACS payment mandate',
    file: 'BACS_Owner-Mandate_Insurance.pdf',
  },
];

const InsurancePage = () => {
  const { loggedInUser, activeAccount, gtmPageViewAuthInfo } = useAuthContext();

  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const { fields, submit } = useForm<InsuranceCertificate>({
    initialState: {
      email: loggedInUser?.email || '',
      fullName: loggedInUser?.name || '',
      insuranceExpiryDate: now().toJSDate(),
      insuranceProvider: '',
      accountNumber: activeAccount?.accountNo || '',
    },
    schema: {
      email: {
        required: true,
        validator: [
          emailValidator,
          createRegexValidator(
            /^((?!haven.com|bourne-leisure.co.uk).)*$/,
            'haven.com and bourne-leisure.co.uk email addresses are not allowed'
          ),
        ],
      },
      fullName: {
        required: true,
      },
      insuranceExpiryDate: {
        required: true,
      },
      insuranceProvider: {
        required: true,
      },
      accountNumber: {
        required: true,
        validator: createRegexValidator(
          /^[a-zA-Z]{2,2}[0-9]{6,6}$/,
          'Account number must contain 2 letters and 6 numbers without any spaces'
        ),
      },
    },
    submitCallback: async (state) => {
      handleSubmit(state);
    },
  });

  useEffect(() => {
    pageView(
      'insurance certificate',
      '/ga-virtual/owners-account/insurance-certificate/enter-details',
      'owners account',
      gtmPageViewAuthInfo
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (formState: InsuranceCertificate) => {
    if (selectedFiles.length < 1 || !formState.insuranceProvider) return;
    setLoading(true);

    const formattedDate = DateTime.fromJSDate(
      formState.insuranceExpiryDate as Date
    ).toFormat('yyyy-MM-dd');
    const data = new FormData();

    data.append(
      'accountNumber',
      activeAccount ? activeAccount.accountNo : formState.accountNumber
    );
    data.append('userType', loggedInUser?.userType || '');
    data.append('fullName', formState.fullName);
    data.append('email', formState.email);
    data.append('insuranceProvider', formState.insuranceProvider);
    data.append('insuranceExpiryDate', formattedDate);
    selectedFiles.forEach((file) => {
      data.append('selectedFiles', file, file.name);
    });

    const analyticsData = {
      ownerAccount: formState.accountNumber,
      insuranceProvider: formState.insuranceProvider.toLowerCase(),
      insuranceExpiry: formattedDate,
    };

    try {
      const { result, error } = await fetchWrapper<CreateInsuranceResponse>(
        withBFFUrl('/freshdesk/insurance'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'multipart/form-data' },
          body: data,
          credentials: 'include',
        }
      );
      if (error) throw new Error(error.message);
      await insuranceConfirmation(
        { ...analyticsData, ...result },
        {
          successFlag: 'success',
        }
      );

      pageView(
        'insurance certificate',
        '/ga-virtual/owners-account/insurance-certificate/confirmation',
        'owners account',
        gtmPageViewAuthInfo
      );
      setSuccess(true);
    } catch (error) {
      setError('Something went wrong, please try again');
      await insuranceConfirmation(analyticsData, {
        successFlag: 'error',
        errorType: error.message.toLowerCase(),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="owners-heading-1">Holiday home insurance</h1>
      <div className={clsx(styles.sectionContent, styles.introSection)}>
        <p className="owners-body">
          As part of your Licence Agreement with Haven you must always have
          valid insurance for your holiday home.
          <br />
          Did you know that there is a Bourne Leisure arranged Insurance policy,
          that many Haven owners benefit from?
          <br />
          We are continuing our association with {''}
          <strong>Aviva Insurance Limited</strong>, one of the largest insurers
          in the UK, so our owners feel confident they have the cover they need.
        </p>
        <div className={styles.avivaLogo}>
          <img
            src="/assets/insurance/aviva-logo.png"
            alt="aviva"
            width={132}
            height={140}
          />
        </div>
      </div>
      <div className={styles.sectionContent}>
        <SectionHeader title="What's covered?" variant="borderBottom" />
        <div className={clsx(styles.contentWrapper, styles.reversed)}>
          <div>
            <p className="owners-body">
              Bourne Leisure Limited arranged insurance policy offers a range of
              insurance protection at an affordable premium to give you peace of
              mind, covering your holiday home and all fixtures and fittings
              contained therein.
              <br />
              <br />
              Accidental damage is covered too, meaning you can claim on your
              insurance for any damaged caused to your holiday home whilst it
              has been let out. We can also cover chalet holiday homes. If your
              holiday home is less than 15 years old, cover can be arranged on a
              new for old basis if required. You can also choose to add personal
              effects cover on your policy for a small additional premium.
              <br />
              <br />
              <span className="no-print">
                Please use the buttons below to download the insurance
                documents.
              </span>
            </p>
            <div className={clsx(styles.infoDownload, 'no-print')}>
              {insuranceDocuments.map((document) => (
                <Button
                  key={document.label}
                  variant="outlined"
                  onClick={() => saveFile(document.file, path)}
                >
                  <img src="https://www.haven.com/assets/icons/download-blue.svg" />
                  <span>{document.label}</span>
                </Button>
              ))}
            </div>
          </div>
          <img
            className="no-print"
            src="/assets/insurance/insurance_1.png"
            alt="What's covered image"
          />
        </div>
      </div>
      <div className={clsx(styles.sectionContent, 'print-avoid-break')}>
        <SectionHeader
          title="Paying for your insurance"
          variant="borderBottom"
        />
        <div className={styles.contentWrapper}>
          <p className="owners-body">
            <strong>
              Your insurance will renew annually in April, and we will issue you
              a new certificate and any other documents as required.
            </strong>
            <br />
            <br />
            The quickest and easiest way to make payment is online on our{' '}
            <a data-testid="financial-page-link" href={'/finance'}>
              finance page
            </a>
            .
          </p>
          <div className={styles.extraContent}>
            <h2 className="owners-heading-2">Ramtech Park Security Alarm</h2>
            <p className="owners-body">
              Park Security alarms are a great way of adding additional security
              to your holiday home.
              <br />
              With this insurance, providing you subscribe to the service, you
              will get a discount on the price of your insurance - we even give
              you a credit to your account if you get one installed after your
              insurance policy start date.
            </p>
            <p>
              <strong>
                Speak to a member of your park team for more information.
              </strong>
            </p>
          </div>
        </div>
      </div>
      <div className={styles.sectionContent}>
        <SectionHeader title="Making a claim" variant="borderBottom" />
        <div className={clsx(styles.contentWrapper, styles.reversed)}>
          <div>
            <p className="owners-body">
              As we have arranged the insurance there is no penalty if you make
              a claim. And more good news is, there is only a small excess,
              currently £50 as defined in the policy.
              <br />
              <br />
              We know that making a claim can be stressful enough, that&apos;s
              why your park team are on hand to help obtain estimates and quotes
              should you need a repair. We deal with contractors to help claims
              go as smoothly as possible.
              <br />
              <br />
              <strong>
                If you have any questions, please do not hesitate to contact
                your park team.
              </strong>
              <br />
              <br />
              <span className="no-print">
                Please use the buttons below to download the form and the
                payment mandate.
              </span>
            </p>
            <div className={clsx(styles.infoDownload, 'no-print')}>
              {claimDocuments.map((document) => (
                <Button
                  key={document.label}
                  variant="outlined"
                  onClick={() => saveFile(document.file, path)}
                >
                  <img src="https://www.haven.com/assets/icons/download-blue.svg" />
                  <span>{document.label}</span>
                </Button>
              ))}
            </div>
          </div>
          <img
            className="no-print"
            src="/assets/insurance/insurance_2.png"
            alt="Making a claim image"
          />
        </div>
      </div>
      <div className={clsx(styles.sectionContent, 'no-print')}>
        <SectionHeader
          title="Arranging your own holiday home insurance"
          variant="borderBottom"
        />
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            <p className="owners-body">
              If you&apos;ve purchased holiday home insurance from another
              provider, please provide us with any details requested below.
              <br />
              Please be aware that we need a copy of your insurance certificate.
              You can provide it by uploading the file from your device or
              taking a photo if you&apos;re using a mobile device.
            </p>

            <form
              className={styles.formWrapper}
              onSubmit={(event) => event.preventDefault()}
            >
              <TextInputField
                id="InsuranceProvider"
                label="Insurance Provider"
                field={fields.insuranceProvider}
                type="text"
              />
              <DateInputField
                id="InsuranceExpiryDate"
                label="Insurance Expiry Date"
                field={fields.insuranceExpiryDate}
                minDate={now().minus({ years: 5 }).toJSDate()}
              />
              <FileUploader
                files={selectedFiles}
                onAdd={(files) => setSelectedFiles(files)}
                onRemove={(files) => setSelectedFiles(files)}
                nthOfFilesRequired={1}
              />
            </form>
            <div className={styles.extraContent}>
              <p className="owners-body">
                <strong>
                  Before uploading your insurance certificate, please make sure
                  it contains the following details:
                </strong>
              </p>
              <ul className={clsx(styles.noticeList, 'owners-body')}>
                <li>
                  Name and address that matches the one on your Haven holiday
                  home ownership account
                </li>
                <li>Valid from and to dates</li>
                <li>Make, model and year of holiday home</li>
                <li>Detailed cover applicable to your holiday home</li>
                <li>Public liability value (minimum of £2 million)</li>
              </ul>
            </div>
            {!success && (
              <Button
                onClick={() => submit()}
                isLoading={loading}
                disabled={selectedFiles.length < 1}
              >
                Submit
              </Button>
            )}
          </div>
          <div className={clsx(styles.extraContent, styles.grey)}>
            <p className="owners-body">
              <strong>Privacy notice:</strong> <br />
              When you submit this form we will process your data to enable us
              to manage your request and respond to you.
              <br />
              We may also keep a record of this communication for an appropriate
              period of time to assist us when managing future communication and
              to demonstrate how we communicated with you.
              <br />
              We do this based on our contractual obligations to our owners, our
              legal obligations and our legitimate interests.
              <br />
              Please see our full privacy policy for more information on how we
              process your personal data.
            </p>
          </div>
        </div>
        {error && (
          <InlineMessaging
            type={InlineMessagingTheme.ERROR_THEME}
            title="Error"
            message={'Something went wrong, please try again.'}
            dismissible={false}
          />
        )}

        {success && (
          <Alert severity="success">
            <strong>
              Thanks, we&apos;ve got those details and a copy of the insurance
              form.
            </strong>
            <br />A member of our team will have a look and make sure we&apos;ve
            got everything we need and update our records. If we need anything
            else, we&apos;ll get in touch.
            <br />
            If you need to submit another policy, please refresh this page and
            fill this form with the new information.
          </Alert>
        )}
      </div>
    </>
  );
};

export const getStaticProps = () => ({
  props: {
    pageTitle: 'Insurance',
  },
  revalidate: 60 * 30,
});

export default InsurancePage;
